/* seic-article-carousel.js — SEIC 文章互動元件漸進增強（零依賴）
   1) 輪播 [data-seic-carousel]：無 JS 時仍可原生手勢橫滑；本檔補藥丸指示條、
      滾輪切換、滑鼠拖曳、鍵盤與 aria（三改移除箭頭；七改移除圖說鏡射、
      藥丸由 CSS 疊到圖片上）
   2) Tab 分頁 [data-seic-tabs]：無 JS 時三張圖直排顯示；本檔補點選切換
   3) 懸浮分類選單 [data-seic-float]：<1024 收合軌的開合（七改：toggle＝
      覆蓋整條黑軌的透明熱區，展開後讓位給連結）、overlay/Esc 收合、回頂鈕
   4) 低解析度圖片守門（十四改）：滿版 figure 若原圖寬度不足以撐 100vw，
      自動掛 .is-lowres 退回文字欄寬，避免放大糊掉（門檻與理由見 guardLowResImages） */
(function () {
  'use strict';

  function initTabs(root) {
    if (root.dataset.seicInit) return;
    root.dataset.seicInit = '1';
    var lis = Array.prototype.slice.call(root.querySelectorAll('.seic-tabs__nav li'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('.seic-tabs__panel'));
    if (lis.length < 2 || lis.length !== panels.length) return;

    var nav = root.querySelector('.seic-tabs__nav');
    nav.setAttribute('role', 'tablist');

    function activate(idx) {
      lis.forEach(function (li, i) {
        li.setAttribute('aria-selected', i === idx ? 'true' : 'false');
        li.setAttribute('tabindex', i === idx ? '0' : '-1');
      });
      panels.forEach(function (p, i) {
        p.classList.toggle('is-active', i === idx);
      });
    }

    lis.forEach(function (li, i) {
      li.setAttribute('role', 'tab');
      li.addEventListener('click', function () { activate(i); });
      li.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); activate(i); }
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          var next = (i + (e.key === 'ArrowRight' ? 1 : lis.length - 1)) % lis.length;
          activate(next);
          lis[next].focus();
        }
      });
    });
    panels.forEach(function (p) { p.setAttribute('role', 'tabpanel'); });

    root.classList.add('seic-tabs--ready');
    activate(0);
  }

  function initCarousel(root) {
    if (root.dataset.seicInit) return;
    root.dataset.seicInit = '1';

    var track = root.querySelector('.seic-carousel__track');
    if (!track) return;
    var slides = Array.prototype.slice.call(track.children);
    if (slides.length < 2) return;

    var prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var scrollBehavior = prefersReduced ? 'auto' : 'smooth';

    root.setAttribute('role', 'region');
    root.setAttribute('aria-roledescription', '輪播');
    root.setAttribute('aria-label', root.getAttribute('aria-label') || '圖片輪播');
    track.setAttribute('tabindex', '0');

    var currentIndex = 0;

    // 目標捲動位置：讓第 i 張靠左貼齊（2026-08-24 十四改，對應 CSS 的
    // scroll-snap-align: start——center 對齊的非循環輪播首末張必然留白，
    // 使用者要求兩端補滿版位）。用「相對第 0 張」而不是絕對 offsetLeft，
    // 這樣 track 日後若再加回 padding 也不會算錯。
    function targetLeft(i) {
      var raw = slides[i].offsetLeft - slides[0].offsetLeft;
      return Math.max(0, Math.min(raw, track.scrollWidth - track.clientWidth));
    }
    function goTo(i, behavior) {
      i = Math.max(0, Math.min(i, slides.length - 1));
      track.scrollTo({ left: targetLeft(i), behavior: behavior || scrollBehavior });
    }

    // 藥丸指示條：active 圓點拉長成條狀（iPhone 式；取代舊圓點與 >5 張 counter）
    var pill = document.createElement('div');
    pill.className = 'seic-carousel__pill';
    var pillInner = document.createElement('div');
    pillInner.className = 'seic-carousel__pill-inner';
    var dotEls = slides.map(function (_slide, i) {
      var d = document.createElement('button');
      d.type = 'button';
      d.className = 'seic-carousel__dot';
      d.setAttribute('aria-label', '第 ' + (i + 1) + ' 張，共 ' + slides.length + ' 張');
      if (i === 0) d.setAttribute('aria-current', 'true');
      d.addEventListener('click', function () { goTo(i); });
      pillInner.appendChild(d);
      return d;
    });
    pill.appendChild(pillInner);
    root.appendChild(pill);

    // （2026-08-24 七改移除圖說鏡射：使用者指示輪播不顯示圖說。
    // 各 slide 的 figcaption 由 CSS 收 sr-only，語意仍保留給螢幕閱讀器。）

    // 螢幕閱讀器進度通知（藥丸沒有文字 counter，補一個 sr-only live region）
    var live = document.createElement('span');
    live.className = 'seic-visually-hidden';
    live.setAttribute('aria-live', 'polite');
    live.textContent = '第 1 張，共 ' + slides.length + ' 張';
    root.appendChild(live);

    function updateActive(idx) {
      if (idx === currentIndex) return;
      currentIndex = idx;
      dotEls.forEach(function (d, i) {
        if (i === idx) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
      slides.forEach(function (s, i) { s.classList.toggle('is-current', i === idx); });
      live.textContent = '第 ' + (idx + 1) + ' 張，共 ' + slides.length + ' 張';
    }
    slides[0].classList.add('is-current');

    // 狀態由 scroll 事件驅動（勿改回 IntersectionObserver——hidden document 不派發）
    var scrollTimer = null;
    function syncFromScroll() {
      var x = track.scrollLeft;
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < slides.length; i++) {
        var d = Math.abs(targetLeft(i) - x);
        if (d < bestDist) { bestDist = d; best = i; }
      }
      updateActive(best);
    }
    track.addEventListener('scroll', function () {
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(syncFromScroll, 100);
    }, { passive: true });

    // 滾輪切換：一次手勢一張＋冷卻；首末張放行（別把整頁的縱向捲動困死）
    var wheelLock = false;
    track.addEventListener('wheel', function (e) {
      if (e.ctrlKey) return; // trackpad 捏合縮放走 ctrl+wheel，放行別當翻頁
      var dominant = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (Math.abs(dominant) < 6) return;
      var dir = dominant > 0 ? 1 : -1;
      if ((dir > 0 && currentIndex === slides.length - 1) || (dir < 0 && currentIndex === 0)) return;
      e.preventDefault();
      if (wheelLock) return;
      wheelLock = true;
      setTimeout(function () { wheelLock = false; }, 450);
      goTo(currentIndex + dir);
    }, { passive: false });

    // 滑鼠拖曳（觸控本來就有原生手勢，只處理 mouse pointer）
    var dragging = false;
    var dragged = false;
    var dragStartX = 0;
    var dragStartScroll = 0;
    track.addEventListener('dragstart', function (e) { e.preventDefault(); }); // 擋原生圖片拖出
    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragging = true;
      dragged = false;
      dragStartX = e.clientX;
      dragStartScroll = track.scrollLeft;
      track.classList.add('is-dragging'); // 關 snap，拖起來才不黏滯
      track.setPointerCapture(e.pointerId);
    });
    track.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var delta = dragStartX - e.clientX;
      if (Math.abs(delta) > 4) dragged = true;
      track.scrollLeft = dragStartScroll + delta;
    });
    function endDrag() {
      if (!dragging) return;
      dragging = false;
      syncFromScroll();
      var delta = track.scrollLeft - dragStartScroll;
      var target = currentIndex;
      // 拖超過 60px 就翻頁（往拖曳方向），不足則彈回當前張
      if (delta > 60 && currentIndex < slides.length - 1) target = currentIndex + 1;
      else if (delta < -60 && currentIndex > 0) target = currentIndex - 1;
      goTo(target);
      // 平滑歸位後再恢復 snap（歸位點＝snap 點，恢復瞬間不會跳）
      setTimeout(function () { track.classList.remove('is-dragging'); }, prefersReduced ? 0 : 380);
    }
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    // 拖曳後吞掉合成 click（保險：slide 內若日後有連結不會誤觸）
    track.addEventListener('click', function (e) {
      if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; }
    }, true);

    track.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(currentIndex + (e.key === 'ArrowLeft' ? -1 : 1));
      } else if (e.key === 'Home') {
        e.preventDefault();
        goTo(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        goTo(slides.length - 1);
      }
    });

    root.classList.add('seic-carousel--ready');
  }

  // 懸浮分類選單 rail：開合狀態單一來源＝.is-open（CSS :hover/:focus-within
  // 只是無 JS fallback，兩者語意一致不衝突）
  function initFloat(root) {
    if (root.dataset.seicInit) return;
    root.dataset.seicInit = '1';

    var nav = root.querySelector('.seic-category-nav');
    var toggle = root.querySelector('[data-seic-float-toggle]');
    var overlay = root.querySelector('[data-seic-float-close]');
    var topBtn = root.querySelector('[data-seic-float-top]');
    if (!nav || !toggle) return;

    var prefersReduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    var hoverable = !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);
    // ≥1024px 是「常駐黑軌」態：四格永遠在畫面上、toggle 與 overlay 都 display:none，
    // 沒有開合這回事（圖卡的推出純 CSS :hover）。開合邏輯只服務 <1024 的收合面板。
    var railMQ = window.matchMedia ? window.matchMedia('(min-width: 1024px)') : null;
    function isRail() { return !!(railMQ && railMQ.matches); }

    function isOpen() { return root.classList.contains('is-open'); }
    function setOpen(v) {
      root.classList.toggle('is-open', v);
      toggle.setAttribute('aria-expanded', v ? 'true' : 'false');
    }

    if (hoverable) {
      nav.addEventListener('pointerenter', function () { if (!isRail()) setOpen(true); });
      nav.addEventListener('pointerleave', function () { if (!isRail()) setOpen(false); });
    }
    toggle.addEventListener('click', function (e) {
      if (isRail()) return;
      // 有滑鼠的裝置由 hover 主導；滑鼠點擊不再 toggle（否則 hover 開啟後
      // 一點反而關閉）。鍵盤 Enter/Space 的合成 click 是 detail 0，放行。
      if (hoverable && e.detail > 0) return;
      setOpen(!isOpen());
    });
    if (overlay) overlay.addEventListener('click', function () { setOpen(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) setOpen(false);
    });

    if (topBtn) {
      topBtn.addEventListener('click', function () {
        window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
      });
      var onScroll = function () {
        topBtn.classList.toggle('is-visible', (window.scrollY || 0) > 600);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  }

  /* 圖片畫質守門（2026-08-24 十四改）
     ── 先修真正的病根：sizes 過期 ──
     pipeline 在 780px 欄寬時代寫死 `sizes="(min-width:1024px) 780px, 100vw"`
     （optimize.py SIZES_VALUE）。版型改滿版後那行沒跟上，瀏覽器**照舊只挑 780px
     的候選**再被 CSS 拉到 1920——srcset 裡明明有 1200/1500 的版本沒被用到。
     實測：naturalWidth 全是 780，而 srcset 最大值是 1200–1500。
     這才是使用者說「糊、馬賽克」的主因，不是原圖不夠大。
     pipeline 那行已同步改成 100vw（未來文章直接是對的）；這裡再對**既有文章**
     即時修正，同事沿用舊 HTML 貼上時也能自動救回。
     代價：改 sizes 後瀏覽器會重挑候選，首次瀏覽可能多下載一次小圖（一次性）。

     ── 再判斷原圖能力 ──
     判定要看 **srcset 的最大候選**（pipeline 產 srcset 時已夾在原圖寬度內，
     那才是「這張圖最高能給到多少像素」）；naturalWidth 只反映「當下載入的那個
     尺寸」，會被 sizes 影響，拿來判斷等於用結果當原因。

     門檻 1400 的取捨：滿版在主流 1920 桌機放大 1920/1400 ≈ 1.37 倍，肉眼輕微、
     可接受；1200 以下（1.6 倍以上）才是使用者說的馬賽克級。太嚴（如要求 2×DPR
     的 3840）會讓幾乎沒有圖能滿版，滿版設計形同虛設。
     只看原圖能力、不看當下視窗寬度——結果可預測，也不必在 resize 時重算。 */
  var FULLBLEED_MIN_SOURCE_WIDTH = 1400;
  var FULLBLEED_SIZES = '100vw';

  function maxSourceWidth(img) {
    var ss = img.getAttribute('srcset');
    if (ss) {
      var widths = [];
      ss.replace(/(\d+)w/g, function (_m, w) { widths.push(+w); return _m; });
      if (widths.length) return Math.max.apply(null, widths);
    }
    return img.naturalWidth || 0; // 沒有 srcset 才退回實際載入尺寸
  }

  function guardLowResImages(root) {
    var figs = root.querySelectorAll('.seic-article > figure');
    Array.prototype.forEach.call(figs, function (fig) {
      var img = fig.querySelector('img');
      if (!img) return;
      var capable = maxSourceWidth(img);
      var lowres = capable > 0 && capable < FULLBLEED_MIN_SOURCE_WIDTH;
      fig.classList.toggle('is-lowres', lowres);
      // 夠格滿版的才把 sizes 開到 100vw（降級的維持窄欄，不必下載大圖）
      if (!lowres && img.getAttribute('srcset') && img.getAttribute('sizes') !== FULLBLEED_SIZES) {
        img.setAttribute('sizes', FULLBLEED_SIZES);
      }
      if (!capable) {
        // srcset 缺失且尚未載入 → 等 load 後用 naturalWidth 再判一次
        img.addEventListener('load', function () {
          fig.classList.toggle('is-lowres', img.naturalWidth > 0 && img.naturalWidth < FULLBLEED_MIN_SOURCE_WIDTH);
        }, { once: true });
      }
    });
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-seic-carousel]'), initCarousel);
    Array.prototype.forEach.call(document.querySelectorAll('[data-seic-tabs]'), initTabs);
    Array.prototype.forEach.call(document.querySelectorAll('[data-seic-float]'), initFloat);
    guardLowResImages(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
